/**
 * A short, readable technical detail for a failed request — for the operator
 * to relay when reporting a problem, never for them to have to understand.
 *
 * Surfaces that use it show the plain message by default and reveal this only
 * on an explicit "Details" toggle. The raw body is unfit for display: a 404
 * from Express arrives as a full HTML document, and rendering it verbatim puts
 * `<!DOCTYPE html> <html lang="en"> <head>…` in front of someone who wanted to
 * know why a list is empty.
 *
 * What survives the cleanup is the part that identifies the fault — Express's
 * `Cannot GET /admin/admin/depots/…/bookings` is exactly how the doubled
 * `/admin` prefix was finally found, and it stays legible once the tags are
 * gone.
 */

/** Longer than this is noise for a UI hint; the cause is always at the front. */
const MAX_DETAIL_LENGTH = 160;

/**
 * `status – message`, or just the status when there is no usable body.
 * Returns null when there is nothing worth showing at all.
 */
export const formatErrorDetail = (error: unknown): string | null => {
    const failure = error as { status?: number | string, data?: unknown } | undefined;
    if (!failure) return null;

    const body = failure.data;
    const raw =
        typeof body === 'string'
            ? body
            : (body as { message?: string, error?: string } | null)?.message
                ?? (body as { message?: string, error?: string } | null)?.error;

    const cleaned = raw
        // An HTML error page reduced to its sentence. Tags out, entities and
        // runs of whitespace collapsed, so `<pre>Cannot GET /x</pre>` becomes
        // `Cannot GET /x` rather than a paragraph of markup.
        ?.replace(/<[^>]*>/g, ' ')
        .replace(/&[a-z]+;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const truncated =
        cleaned && cleaned.length > MAX_DETAIL_LENGTH
            ? `${cleaned.slice(0, MAX_DETAIL_LENGTH)}…`
            : cleaned;

    const parts = [failure.status, truncated].filter(Boolean);
    return parts.length ? parts.join(' – ') : null;
};
